import React, { useCallback, useMemo, useState } from "react";
import { Box, Heading, HStack } from "@chakra-ui/react";
import Card from "@/src/components/card/Card";
import { Button } from "@/src/components/ui/button";
import { Input } from "@/src/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/src/components/ui/table";
import { useMutation } from "@tanstack/react-query";
import { Post } from "@/src/api";
import { useToast } from "@/src/hooks/use-toast";
import { Redirect } from "react-router-dom";
import { ClipboardPaste, Plus, Trash2 } from "lucide-react";
import {
  BulkAddCoinsItem,
  BulkAddCoinsRequest,
} from "@/src/types/bulkAddCoins";
import useSuperAdmin from "@/src/hooks/useSuperAdmin";

const NOTE_MAX_LENGTH = 255;

interface RowState {
  id: string;
  userId: string;
  coins: string;
  packageName: string;
  note: string;
}

const generateRowId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `row-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const createEmptyRow = (): RowState => ({
  id: generateRowId(),
  userId: "",
  coins: "",
  packageName: "",
  note: "",
});

const isRowEmpty = (row: RowState): boolean =>
  !row.userId && !row.coins && !row.packageName && !row.note;

const splitLineCells = (line: string): string[] => {
  if (line.includes("\t")) return line.split("\t");
  if (line.includes(",")) return line.split(",");
  return [line];
};

const parsePastedRows = (text: string): RowState[] => {
  const rows: RowState[] = [];

  for (const rawLine of text.split(/\r?\n/)) {
    if (rawLine.trim().length === 0) continue;
    const cells = splitLineCells(rawLine).map((c) => c.trim());

    rows.push({
      id: generateRowId(),
      userId: cells[0] ?? "",
      coins: cells[1] ?? "",
      packageName: cells[2] ?? "",
      note: cells[3] ?? "",
    });
  }

  return rows;
};

const isRowValid = (row: RowState): boolean =>
  row.userId.trim().length > 0 && row.coins.trim().length > 0;

export default function BulkAddCoinsPage() {
  const isSuperAdmin = useSuperAdmin();
  const { toast } = useToast();

  const [rows, setRows] = useState<RowState[]>(() => [createEmptyRow()]);
  const [showPaste, setShowPaste] = useState(false);
  const [pasteText, setPasteText] = useState("");

  const validCount = useMemo(() => rows.filter(isRowValid).length, [rows]);
  const allValid = rows.length > 0 && validCount === rows.length;

  const updateRow = useCallback(
    (id: string, patch: Partial<Omit<RowState, "id">>) => {
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, ...patch } : r)),
      );
    },
    [],
  );

  const addRow = useCallback(() => {
    setRows((prev) => [...prev, createEmptyRow()]);
  }, []);

  const removeRow = useCallback((id: string) => {
    setRows((prev) =>
      prev.length === 1 ? prev : prev.filter((r) => r.id !== id),
    );
  }, []);

  const clearAll = useCallback(() => {
    setRows([createEmptyRow()]);
  }, []);

  const appendParsedRows = useCallback(
    (parsed: RowState[]) => {
      if (parsed.length === 0) {
        toast({
          title: "Nothing to paste",
          description: "No usable rows were found in the pasted text.",
          variant: "default",
        });
        return;
      }
      setRows((prev) => {
        if (prev.length === 1 && isRowEmpty(prev[0])) {
          return parsed;
        }
        return [...prev, ...parsed];
      });
      toast({
        title: "Rows added",
        description: `Added ${parsed.length} row${
          parsed.length === 1 ? "" : "s"
        } from pasted text.`,
        variant: "success",
      });
    },
    [toast],
  );

  const handleApplyPaste = useCallback(() => {
    const parsed = parsePastedRows(pasteText);
    appendParsedRows(parsed);
    setPasteText("");
    setShowPaste(false);
  }, [pasteText, appendParsedRows]);

  const handleCancelPaste = useCallback(() => {
    setPasteText("");
    setShowPaste(false);
  }, []);

  const mutation = useMutation<unknown, Error, BulkAddCoinsItem[]>({
    mutationFn: (userCoins) => {
      const body: BulkAddCoinsRequest = { userCoins };
      return Post("/support/user/add-coins/bulk", body);
    },
    onSuccess: (_data, variables) => {
      setRows([createEmptyRow()]);
      toast({
        title: "Success",
        description: `Submitted ${variables.length} row${
          variables.length === 1 ? "" : "s"
        }`,
        variant: "success",
      });
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Failed to add coins",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = useCallback(() => {
    if (!allValid) {
      toast({
        title: "Missing fields",
        description: "Each row needs a User ID and Coins value.",
        variant: "destructive",
      });
      return;
    }
    const items: BulkAddCoinsItem[] = rows.map((r) => ({
      userId: parseInt(r.userId, 10),
      coins: parseInt(r.coins, 10),
      packageName: r.packageName.trim() ? r.packageName.trim() : null,
      note: r.note.trim(),
    }));
    mutation.mutate(items);
  }, [rows, allValid, mutation, toast]);

  if (!isSuperAdmin) {
    return <Redirect to={"/"} />;
  }

  return (
    <Box>
      <Card>
        <Box p={4}>
          <HStack justify="space-between" mb={4} align="center">
            <Heading size="md">Bulk Add Coins</Heading>
            <HStack spacing={2}>
              <Button
                onClick={() => setShowPaste((s) => !s)}
                variant="outline"
                size="sm"
              >
                <ClipboardPaste className="mr-1 h-4 w-4" />
                Paste rows
              </Button>
              <Button onClick={addRow} variant="outline" size="sm">
                <Plus className="mr-1 h-4 w-4" />
                Add row
              </Button>
            </HStack>
          </HStack>

          {showPaste && (
            <div className="mb-4 rounded-md border bg-gray-50 p-3">
              <textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder={
                  "1\t250\tcom.global.pnck\tnote1\n" +
                  "2\t250\tio.playtime\tnote2\n" +
                  "..."
                }
                rows={6}
                autoFocus
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
              <div className="mt-2 flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={handleCancelPaste}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleApplyPaste}
                  disabled={!pasteText.trim()}
                >
                  Apply
                </Button>
              </div>
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[180px]">User ID</TableHead>
                <TableHead className="w-[180px]">Coins</TableHead>
                <TableHead>Package Name (optional)</TableHead>
                <TableHead>Note (optional)</TableHead>
                <TableHead className="w-[80px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row: RowState) => {
                return (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Input
                        type="number"
                        placeholder="User ID"
                        value={row.userId}
                        onChange={(e) =>
                          updateRow(row.id, { userId: e.target.value })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        placeholder="Coins"
                        value={row.coins}
                        onChange={(e) =>
                          updateRow(row.id, { coins: e.target.value })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        placeholder="com.example.app"
                        value={row.packageName}
                        onChange={(e) =>
                          updateRow(row.id, { packageName: e.target.value })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        placeholder="Note"
                        value={row.note}
                        maxLength={NOTE_MAX_LENGTH}
                        onChange={(e) =>
                          updateRow(row.id, { note: e.target.value })
                        }
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={rows.length === 1}
                        onClick={() => removeRow(row.id)}
                        aria-label="Remove row"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <Box
            mt={4}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            gap={2}
          >
            <HStack spacing={2}>
              <Button
                variant="outline"
                onClick={clearAll}
                disabled={mutation.isPending}
              >
                Clear all
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={mutation.isPending}
                isLoading={mutation.isPending}
              >
                Submit ({rows.length} {rows.length === 1 ? "row" : "rows"})
              </Button>
            </HStack>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}
